import {IMachine, TimeSeriesObject, TimeSeriesStringObject} from '../api/domain-model';
import {ILineChartData, ILineChartData4Multiline, TimePeriod} from '../d3-charts/chart-domains';
import {IChartValue, IColumnChartOptions, IColumnChartOptionsWithModel, IGaugeChartOptions as IDomainModelGaugeChartOptions} from '../charts/domain-model';
import {IGaugeChartOptions as IChartDomainGaugeChartOptions} from 'app/d3-charts/chart-domains';
import {Subject} from 'rxjs';

interface IStepValueHolder {
	stepValue: number;
}

interface INumberValueStringValueHolder {
	[key: string]: number;
}

export default class Utils {
	/**
	 *
	 * @param identifier eg./0030565002C0/sDynValue3
	 * @private checks whether the identifier contains 'sDynValue'
	 */
	public static isIdentifierStringVariable(identifier: string) {
		const regex = /\/[A-Za-z0-9-]+\/sDynValue\d/i;
		return (
			regex.test(identifier) ||
			// not a good way, but for TYP-2433 & TYP-2447
			this.variableStartsWithSButNotWithS32(identifier)
		);
	}

	private static variableStartsWithSButNotWithS32(identifier: string) {
		let variable = this.extractVariableFromIdentifier(identifier);
		return variable?.startsWith('s') && !variable?.startsWith('s32');
	}

	/**
	 *
	 * @param identifier eg. /003056500472/sCurrentType
	 * returns sCurrentType
	 */
	private static extractVariableFromIdentifier(identifier: string) {
		const parts = identifier?.split('/');
		const slashCount = parts?.length - 1;

		if (slashCount !== 2) {
			throw new Error('Not a valid identifier: ' + identifier);
		}

		return parts[2];
	}

	/**
	 * Adds arbitrary number value as the array contains only string values.
	 * Every change in the string value adds a new number value to the array.
	 * @param timeSeriesStringObjects
	 */
	public static populateValueFromString(timeSeriesStringObjects: Array<Array<TimeSeriesStringObject>>): Array<Array<TimeSeriesStringObject>> {
		if (!timeSeriesStringObjects) {
			return null;
		}

		return timeSeriesStringObjects.map((timeSeriesStringObjectArray) => {

			const currentArray = (timeSeriesStringObjectArray || []).map(obj => ({...obj}));
			const nonNullValues = currentArray.filter(x => x.value !== null && x.value !== undefined && x.value.toString() !== 'null');
			const isStringData = nonNullValues.every(x => typeof x.value === 'string' && isNaN(Number(x.value)));

			if (isStringData) {
				// this two variables are used as cache
				// we need to initiate for every metric, so this is inside of loop
				const stepValueHolder = {stepValue: 0} as IStepValueHolder; // Passing by object to persist value once passed into function.
				const numberValueStringValueHolder = {} as INumberValueStringValueHolder;
				currentArray.forEach((timeSeriesStringObject, index, wholeArray) => {
					this.populateStringValueInTimeSeriesObject(timeSeriesStringObject, numberValueStringValueHolder, stepValueHolder);
				});
			} else {
				currentArray.forEach((timeSeriesStringObject, index, wholeArray) => {
					this.populateNumberStringValueInTimeSeriesObject(timeSeriesStringObject);
				});
			}

			return currentArray;
		});

	}

	public static createTimePeriod(timestampStart: number, timestampEnd: number): TimePeriod {
		return {
			timestampStart,
			timestampEnd
		} as TimePeriod;
	}

	public static mapDynamicTableUnitNumberToString(unitInt) {
		const textKey = [
			'A430kg',
			'A431lb',
			'A432t',
			'A433klb',
			'A434g',
			'A629Percent',
			'A124Minute',
			'A122Seconds',
			'A628UnitperH',
			'A642Hours',
			'A643Temp',
			'A644Millisecond',
			'A645KgPerSecond',
			'A646RoundsperMinute',
			'A647Meter',
			'A648Millimeter',
			'A649Cubicmeter',
			'A650Liter',
			'A651Counts',
			'A750GPerSecond'
		];
		return textKey[unitInt];
	}

	/**
	 * Transformiert einen String-basierten Datenpunkt in einen numerischen Wert für die Chart-Visualisierung.
	 * Da Diagramm-Komponenten (z. B. Step-Charts) für die Y-Achse numerische Koordinaten benötigen,
	 * weist diese Methode jedem eindeutigen String-Wert eine diskrete Zahl (in 5er-Schritten) zu.
	 * Der Original-String wird parallel im Feld `stringValue` gesichert.
	 *
	 * @param timeSeriesStringObject - Das Datenpunkt-Objekt aus der Zeitreihe.
	 * Eingabe: Erwartet den String im Feld `value`.
	 * Ausgabe: `value` wird zur Zahl (Zustands-ID), `stringValue` enthält den Original-Text.
	 * @param numberValueStringValueHolder - Ein Cache-Objekt (Map), das bereits zugewiesene
	 * String-Zahlen-Paare speichert, um Konsistenz über die gesamte Zeitreihe zu gewährleisten.
	 *  @param stepValueHolder - Ein zustandsbehaftetes Objekt, das den aktuellen Zähler für die
	 * Zustands-IDs verwaltet (wird bei jedem neuen String-Typ um 5 erhöht).
	 * @example
	 * // Erster Aufruf mit "Typ A" -> value wird 5, stringValue wird "Typ A"
	 * // Zweiter Aufruf mit "Typ B" -> value wird 10, stringValue wird "Typ B"
	 * // Dritter Aufruf mit "Typ A" -> value wird 5 (aus Cache), stringValue wird "Typ A"
	 */
	private static populateStringValueInTimeSeriesObject(
		timeSeriesStringObject: TimeSeriesStringObject,
		numberValueStringValueHolder: INumberValueStringValueHolder,
		stepValueHolder: IStepValueHolder
	) {
		const stringValue = timeSeriesStringObject.value ? timeSeriesStringObject.value.toString() : '';

		// Prüfen, ob dieser String schon eine Zahl zugewiesen bekommen hat
		if (numberValueStringValueHolder[stringValue] !== undefined) {
			timeSeriesStringObject.value = numberValueStringValueHolder[stringValue];
		} else {
			// Erster String bekommt 5, zweiter 10, etc.
			stepValueHolder.stepValue += 5;
			numberValueStringValueHolder[stringValue] = stepValueHolder.stepValue;
			timeSeriesStringObject.value = stepValueHolder.stepValue;
		}

		timeSeriesStringObject.stringValue = stringValue;
	}

	private static populateNumberStringValueInTimeSeriesObject(timeSeriesStringObject: TimeSeriesStringObject) {
		const numberValue = (isNaN(+timeSeriesStringObject.value) ? 0 : +timeSeriesStringObject.value);
		timeSeriesStringObject.stringValue = String(timeSeriesStringObject.value);
		timeSeriesStringObject.value = numberValue;
		return;
	}

	public static isPrefixInNeededValues(neededValues: Array<Array<string>>, prefix: string): boolean {
		if (prefix === '' || !neededValues) {
			return false;
		}
		const flattenValues = neededValues.flat();
		if (flattenValues.length <= 0) {
			return false;
		}
		return flattenValues.some((value) => value.startsWith(prefix));
	}

	/**
	 * Recursively checks if any key in the given object (or its nested objects/arrays) starts with the specified prefix.
	 * @param obj The object or array to search.
	 * @param prefix The prefix to look for in the keys.
	 * @returns True if any key starts with the prefix, otherwise false.
	 */
	public static isPrefixInObject(obj: any, prefix: string): boolean {
		if (obj === null || obj === undefined || typeof obj !== 'object') {
			return false;
		}

		if (Array.isArray(obj)) {
			return obj.some(item => this.isPrefixInObject(item, prefix));
		}

		for (const key of Object.keys(obj)) {
			if (key.startsWith(prefix)) {
				return true;
			}
			if (this.isPrefixInObject(obj[key], prefix)) {
				return true;
			}
		}

		return false;
	}

	public static getMqttPrefixFromMachine(machine: IMachine): string {
		//return machine ? `${machine.companyId}/${machine.name}` : 'NOMACHINE';
		return machine ? `${machine.name}` : 'NOMACHINE';
	}

	// ! This is used to avoid Typescript errors in the templates
	// ! In future we should fix the castings
	// ! Because this "castTo<T>" is nothing more than an any cast
	private static castTo<T>(): (input) => T {
		return (input) => input as T;
	}
	private static transformGaugeChartOptions(input: IDomainModelGaugeChartOptions): IChartDomainGaugeChartOptions {
		return Utils.castTo<IChartDomainGaugeChartOptions>()(input);
	}
	private static transformColumnChartOptions(input: IColumnChartOptions): IColumnChartOptionsWithModel {
		return Utils.castTo<IColumnChartOptionsWithModel>()(input);
	}
	private static transformArrayInDoubleArray(input: Array<string>): Array<Array<string>> {
		// This is used by neededValues
		return Utils.castTo<Array<Array<string>>>()(input);
	}
	private static transformDoubleArrayInArray(input: Array<Array<string>>): Array<string> {
		return Utils.castTo<Array<string>>()(input);
	}
	private static transformString2Number(input: string): number {
		return input ? parseFloat(input) : null;
	}
	public static transformD3MultiLineTimeSeriesData(input: Subject<IChartValue[]>): ILineChartData[][] {
		return Utils.castTo<ILineChartData[][]>()(input);
	}
	public static transformD3MultiLine4MultilineTimeSeriesData(input: Subject<IChartValue[]>): ILineChartData4Multiline[] {
		return Utils.castTo<ILineChartData4Multiline[]>()(input);
	}
	public static transformMachineReport_SelectedHistoryItems(input: Record<string, unknown>[]): string[] {
		return Utils.castTo<string[]>()(input);
	}
	public static transformD3Multiline_TimeSeriesData(input: TimeSeriesObject[][]): Array<Array<ILineChartData>> {
		return Utils.castTo<Array<Array<ILineChartData>>>()(input);
	}
	public static transformD3Multiline4Multiline_TimeSeriesData(input: TimeSeriesObject[][]): Array<ILineChartData4Multiline> {
		return Utils.castTo<Array<ILineChartData4Multiline>>()(input);
	}
	public static Cast = {
		transformGaugeChartOptions: Utils.transformGaugeChartOptions,
		transformColumnChartOptions: Utils.transformColumnChartOptions,
		transformArrayInDoubleArray: Utils.transformArrayInDoubleArray,
		transformDoubleArrayInArray: Utils.transformDoubleArrayInArray,
		transformString2Number: Utils.transformString2Number,
		transformD3MultiLineTimeSeriesData: Utils.transformD3MultiLineTimeSeriesData,
		transformD3MultiLine4MultilineTimeSeriesData: Utils.transformD3MultiLine4MultilineTimeSeriesData,
		transformMachineReport_SelectedHistoryItems: Utils.transformMachineReport_SelectedHistoryItems,
		transformD3Multiline_TimeSeriesData: Utils.transformD3Multiline_TimeSeriesData,
		transformD3Multiline4Multiline_TimeSeriesData: Utils.transformD3Multiline4Multiline_TimeSeriesData
	};
}
